import Layout from "@/components/LandingPage/Layout";
import {
  Button,
  Form,
  Input,
  Spin,
  Typography,
  Radio,
  Divider,
  Modal,
} from "antd";
import React, { useState, useEffect } from "react";
import { PhoneNumberUtil } from "google-libphonenumber";
import { ContactFormRole } from "@/enums/contact-form-role.enum";
import { getInTouchForm } from "@/api/core";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import CountryCodeSelect from "@/components/Common/CountryCodeSelect";
import PhoneInput from "@/components/Common/PhoneInput";
import Right_Tick from "@/public/images/Right_tick.webp";
import Image from "next/image";

const ContactUs = () => {
  const [contactForm] = Form.useForm();
  const countryCode = Form.useWatch("countryCode", contactForm);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const phoneUtil = PhoneNumberUtil.getInstance();
  const roleOptions = [
    { label: "Parent", value: ContactFormRole.PARENT },
    { label: "Educator", value: ContactFormRole.Educator },
    { label: "Partner", value: ContactFormRole.Partner },
  ];
  const [showPopupForSuccessContact, setShowPopupForSuccessContact] =
    useState(false);

  useEffect(() => {
    contactForm.setFieldValue("countryCode", "+1");
  }, []);

  const handleContactSubmit = (values: any) => {
    const { countryCode, phone, ...rest } = values;
    setContactSubmitting(true);
    const payload = phone
      ? { ...rest, phone: countryCode + phone }
      : { ...rest };
    getInTouchForm(payload).then((res) => {
      setContactSubmitting(false);
      setShowPopupForSuccessContact(true);
    });
  };

  const handleCloseSuccessPopup = () => {
    setShowPopupForSuccessContact(false);
    contactForm.resetFields();
  };

  const onSelectCountryCode = (val: any) => {
    contactForm.setFieldValue("countryCode", val);
    contactForm.validateFields(["countryCode"]);
    const phoneValue = contactForm.getFieldValue("phone");
    if (phoneValue) {
      contactForm.validateFields(["phone"]);
    }
  };

  const validatePhoneNumber = (_, value) => {
    if (!value) return Promise.resolve();

    if (!countryCode) {
      return Promise.resolve();
    }

    try {
      const phoneNumberWithCode = `${countryCode}${value}`;
      const number = phoneUtil.parseAndKeepRawInput(phoneNumberWithCode, "");
      return phoneUtil.isValidNumber(number)
        ? Promise.resolve()
        : Promise.reject("Enter valid phone number.");
    } catch {
      return Promise.reject("Enter valid phone number.");
    }
  };

  return (
    <Layout>
      {" "}
      <section className="py-10 bg-white">
        <div className="lg:w-[85%] md:w-[87%] w-[90%] mx-auto">
          {/* Right Form - More compact */}
          <div className="w-full max-w-2xl mx-auto">
            {/* Left Content - Aligned with form */}
            <Typography.Title
              style={{ color: "#2D68C4" }}
              className="!text-[24px] text-center sm:!text-[30px] md:!text-[38px] mb-8"
            >
              {`Get in touch with us. We're here to assist you.`}
            </Typography.Title>

            <Form
              layout="vertical"
              className="w-full"
              form={contactForm}
              onFinish={handleContactSubmit}
              scrollToFirstError
              name="Contact information"
              disabled={contactSubmitting}
              initialValues={{ countryCode: "+1" }}
              id="contact-information"
            >
              <Form.Item
                label={"Name"}
                name="name"
                rules={[
                  { required: true, message: "Please enter your name" },
                  {
                    min: 2,
                    message: "Name must be at least 2 characters long",
                  },
                  { max: 100, message: "Name cannot exceed 100 characters" },
                  {
                    pattern: /^[A-Za-z\s]+$/,
                    message: "Name can only contain letters and spaces",
                  },
                ]}
                validateTrigger={["onBlur", "onChange"]}
                className="mb-4"
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400 mr-1" />}
                  placeholder="Enter your name"
                  status={
                    contactForm.getFieldError("name").length > 0 ? "error" : ""
                  }
                  size="middle"
                  className="h-10"
                />
              </Form.Item>

              <Form.Item
                label={"Email"}
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                  { max: 254, message: "Email address is too long" },
                ]}
                validateTrigger={["onBlur", "onChange"]}
                className="mb-4"
              >
                <Input
                  prefix={<MailOutlined className="text-gray-400 mr-1" />}
                  placeholder="Enter your email"
                  status={
                    contactForm.getFieldError("email").length > 0 ? "error" : ""
                  }
                  size="middle"
                  className="h-10"
                />
              </Form.Item>

              <Form.Item
                label="You Are"
                name="role"
                rules={[{ required: true, message: "please enter your role" }]}
                validateTrigger={["onBlur", "onChange"]}
                className="mb-4"
              >
                <Radio.Group
                  name="contact-info-radio-group"
                  options={roleOptions}
                  className="flex flex-wrap gap-4"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Phone No. <span className="opacity-40">(optional)</span>
                  </span>
                }
                className="mb-4 w-full"
              >
                <div className="flex items-center gap-2">
                  <Form.Item name="countryCode" className="min-w-32 mb-0">
                    <CountryCodeSelect
                      onSelectCountryCode={onSelectCountryCode}
                      field={{ value: countryCode }}
                      isDisabled={true}
                    />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    rules={[
                      {
                        validator: validatePhoneNumber,
                      },
                    ]}
                    className="flex-1 mb-0"
                    validateTrigger={["onBlur", "onChange"]}
                  >
                    <PhoneInput
                      placeholder="Enter your phone number"
                      countryCode={countryCode}
                      size="middle"
                      className="h-10"
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                label={"Message"}
                name="message"
                rules={[
                  {
                    min: 10,
                    message: "Message must be at least 10 characters long",
                  },
                  {
                    max: 1000,
                    message: "Message cannot exceed 1000 characters",
                  },
                ]}
                validateTrigger={["onBlur", "onChange"]}
                className="mb-6"
              >
                <Input.TextArea
                  placeholder="Enter your message"
                  rows={5}
                  maxLength={1000}
                  showCount
                  status={
                    contactForm.getFieldError("message").length > 0
                      ? "error"
                      : ""
                  }
                  className="resize-none"
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-10 w-full bg-[#2D68C4] hover:bg-[#245a9f] transition-colors"
                  loading={
                    contactSubmitting
                      ? { icon: <Spin size="small" className="spin-white" /> }
                      : false
                  }
                  disabled={contactSubmitting}
                >
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </div>
          <Divider className="mb-0 mt-8" />
        </div>
      </section>
      {showPopupForSuccessContact && (
        <Modal
          title={null}
          open={showPopupForSuccessContact}
          footer={null}
          centered
          onCancel={handleCloseSuccessPopup}
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <Image
              src={Right_Tick}
              alt="Right_Tick_Contact_Us"
              width={100}
              height={100}
            />
            <Typography.Title level={4} className="text-center !text-[#2D68C4]">
              Message Received!
            </Typography.Title>
            <Typography.Paragraph className="text-center">
              {`Thank you for reaching out to StedPrep. Our team has received your message and will be in touch with you soon.`}
            </Typography.Paragraph>
            <Button
              type="primary"
              htmlType="button"
              className="bg-[#2D68C4]"
              onClick={handleCloseSuccessPopup}
            >
              Close
            </Button>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default ContactUs;
